/**
 * Resize Utilities
 * Handles resize calculations for layer scaling
 */

export type HandlePosition = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

export interface ResizeBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Calculate new bounds after a resize operation
 *
 * Handle behavior:
 * | Handle | Changes              | Anchor         |
 * |--------|----------------------|----------------|
 * | nw     | x, y, width, height  | bottom-right   |
 * | ne     | y, width, height     | bottom-left    |
 * | sw     | x, width, height     | top-right      |
 * | se     | width, height        | top-left       |
 * | n      | y, height            | bottom         |
 * | s      | height               | top            |
 * | e      | width                | left           |
 * | w      | x, width             | right          |
 */
export function calculateNewBounds(
  initial: ResizeBounds,
  handle: HandlePosition,
  deltaX: number,
  deltaY: number,
  constrainAspect: boolean,
  aspectRatio: number
): ResizeBounds {
  let { x, y, width, height } = initial;

  // Apply delta based on handle position
  switch (handle) {
    case 'se':
      // Bottom-right: anchor top-left, grow width/height
      width += deltaX;
      height += deltaY;
      break;

    case 'sw':
      // Bottom-left: anchor top-right, grow height, shrink width (move x)
      x += deltaX;
      width -= deltaX;
      height += deltaY;
      break;

    case 'ne':
      // Top-right: anchor bottom-left, shrink height (move y), grow width
      y += deltaY;
      width += deltaX;
      height -= deltaY;
      break;

    case 'nw':
      // Top-left: anchor bottom-right, move x/y, shrink width/height
      x += deltaX;
      y += deltaY;
      width -= deltaX;
      height -= deltaY;
      break;

    case 'n':
      // Top: anchor bottom, move y, shrink height
      y += deltaY;
      height -= deltaY;
      break;

    case 's':
      // Bottom: anchor top, grow height
      height += deltaY;
      break;

    case 'e':
      // Right: anchor left, grow width
      width += deltaX;
      break;

    case 'w':
      // Left: anchor right, move x, shrink width
      x += deltaX;
      width -= deltaX;
      break;
  }

  // Constrain aspect ratio if requested
  if (constrainAspect && aspectRatio > 0) {
    const currentAspect = width / height;

    // Determine which dimension to adjust based on handle and current state
    if (handle === 'n' || handle === 's') {
      // Vertical handles: adjust width to match
      width = height * aspectRatio;
    } else if (handle === 'e' || handle === 'w') {
      // Horizontal handles: adjust height to match
      height = width / aspectRatio;
    } else {
      // Corner handles: use the larger dimension change
      if (currentAspect > aspectRatio) {
        // Width grew more - adjust height
        height = width / aspectRatio;
      } else {
        // Height grew more - adjust width
        width = height * aspectRatio;
      }
    }

    // Adjust position for handles that move the origin
    if (handle === 'nw' || handle === 'ne' || handle === 'n') {
      // Recalculate y based on new height
      y = initial.y + initial.height - height;
    }
    if (handle === 'nw' || handle === 'sw' || handle === 'w') {
      // Recalculate x based on new width
      x = initial.x + initial.width - width;
    }
  }

  return { x, y, width, height };
}

/**
 * Get the appropriate cursor for a resize handle
 */
export function getHandleCursor(handle: HandlePosition): string {
  switch (handle) {
    case 'nw':
    case 'se':
      return 'nwse-resize';
    case 'ne':
    case 'sw':
      return 'nesw-resize';
    case 'n':
    case 's':
      return 'ns-resize';
    case 'e':
    case 'w':
      return 'ew-resize';
    default:
      return 'default';
  }
}

/**
 * Check if a handle is a corner handle
 */
export function isCornerHandle(handle: HandlePosition): boolean {
  return handle === 'nw' || handle === 'ne' || handle === 'sw' || handle === 'se';
}

/**
 * Check if a handle is an edge handle
 */
export function isEdgeHandle(handle: HandlePosition): boolean {
  return handle === 'n' || handle === 's' || handle === 'e' || handle === 'w';
}
